import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, CheckCircle2, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { SLOTS, TOPICS } from "../data/content";

function nextDays(count = 14) {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    if (d.getDay() !== 0) {
      days.push({
        iso: d.toISOString().slice(0, 10),
        weekday: d.toLocaleDateString("en-IN", { weekday: "short" }),
        day: d.getDate(),
        month: d.toLocaleDateString("en-IN", { month: "short" }),
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
}

export default function Booking() {
  const days = useMemo(() => nextDays(), []);
  const [page, setPage] = useState(0);
  const [date, setDate] = useState(days[0].iso);
  const [taken, setTaken] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", topic: TOPICS[0], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState("");
  const [payConfig, setPayConfig] = useState({ enabled: false });
  const [sessionType, setSessionType] = useState("discovery");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setPayConfig(data)).catch(() => setPayConfig({ enabled: false }));
  }, []);

  useEffect(() => {
    let live = true;
    setSlotsLoading(true);
    api
      .get(`/bookings/availability`, { params: { date } })
      .then(({ data }) => live && setTaken(data.taken || []))
      .catch(() => live && setTaken([]))
      .finally(() => live && setSlotsLoading(false));
    return () => {
      live = false;
    };
  }, [date]);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const openCheckout = async (booking) => {
    setPaying(true);
    setError("");
    const ok = await loadRazorpay();
    if (!ok) {
      setError("Could not load the payment window. Check your connection and try again.");
      setConfirmed({ ...booking, payment_status: "pending" });
      setPaying(false);
      return;
    }
    try {
      const { data: order } = await api.post("/payments/order", { booking_id: booking.id });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Sundeepa Bamval",
        description: "Online consultation — 1 hour",
        order_id: order.order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#0066cc" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              booking_id: booking.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setConfirmed({ ...booking, payment_status: "paid" });
          } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Payment verification failed. Contact us if money was deducted.");
            setConfirmed({ ...booking, payment_status: "pending" });
          }
          setPaying(false);
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setConfirmed({ ...booking, payment_status: "pending" });
          },
        },
      });
      rzp.open();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setConfirmed({ ...booking, payment_status: "pending" });
      setPaying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/bookings", { ...form, date, session_type: sessionType });
      if (sessionType === "consultation" && payConfig?.enabled) {
        await openCheckout(data);
      } else {
        setConfirmed(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const pickSlot = (slot) => setForm((f) => ({ ...f, slot }));
  const visibleDays = days.slice(page * 7, page * 7 + 7);

  if (confirmed) {
    return (
      <section id="booking" className="py-20 sm:py-28 lg:py-32 scroll-mt-16" data-testid="booking-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="bg-white rounded-[28px] border border-[#e5e5e7] shadow-[0_10px_40px_rgba(0,0,0,0.05)] p-10 sm:p-14 text-center"
            data-testid="booking-confirmation-alert"
          >
            <span className="mx-auto w-16 h-16 rounded-full bg-[#eef6ee] border border-[#dceadc] flex items-center justify-center text-[#3d7a3d]">
              <CheckCircle2 size={30} />
            </span>
            <h3 className="mt-6 text-3xl font-semibold tracking-tight">
              {confirmed.payment_status === "paid" ? "Booked & paid" : "You're booked in"}
            </h3>
            <p className="mt-3 text-[#86868b] leading-relaxed">
              {new Date(confirmed.date + "T00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              {" · "}{confirmed.slot} IST. A confirmation email is on its way — Sundeepa will share the meeting link on WhatsApp or by email.
            </p>
            {confirmed.payment_status === "paid" && (
              <p className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#eef6ee] text-[#3d7a3d] text-sm font-medium" data-testid="booking-paid-chip">
                <CheckCircle2 size={15} /> ₹7,000 paid — see you there
              </p>
            )}
            {confirmed.payment_status === "pending" && (
              <div className="mt-5" data-testid="booking-pending-payment">
                <p className="text-sm text-[#9a6b1f]">Your slot is reserved — complete the ₹7,000 consultation payment to lock it in.</p>
                <button
                  onClick={() => openCheckout(confirmed)}
                  disabled={paying}
                  data-testid="booking-complete-payment-btn"
                  className="mt-3 px-6 py-3 rounded-full bg-[#0066cc] text-white text-sm font-medium hover:bg-[#0077ed] transition-all disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {paying ? <Loader2 size={15} className="animate-spin" /> : null}
                  {paying ? "Opening payment..." : "Complete payment"}
                </button>
              </div>
            )}
            <button
              data-testid="booking-another-btn"
              onClick={() => { setConfirmed(null); setForm({ name: "", email: "", phone: "", company: "", topic: TOPICS[0], message: "" }); }}
              className="mt-8 px-6 py-3 rounded-full border border-[#d2d2d7] text-sm font-medium hover:bg-[#f5f5f7] transition-all"
            >
              Book another call
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-20 sm:py-28 lg:py-32 scroll-mt-16 bg-white" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <SectionHeading
          eyebrow="Booking"
          title="Book a discovery call"
          subtitle="Book consultation about your collection, your calendar and where the margin is leaking. Slots are in IST."
        />

        <div className="mt-12 sm:mt-16 grid lg:grid-cols-[0.9fr_1.1fr] gap-6 items-start">
          <Reveal className="bg-[#f5f5f7] rounded-[28px] border border-[#e5e5e7] p-7 sm:p-9">
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#1d1d1f]">
                <CalendarDays size={17} className="text-[#0066cc]" /> Pick a date
              </p>
              <div className="flex gap-1">
                <button
                  aria-label="Previous days" data-testid="booking-days-prev"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="p-2 rounded-full hover:bg-white disabled:opacity-30 transition-all"
                >
                  <ChevronLeft size={17} />
                </button>
                <button
                  aria-label="Next days" data-testid="booking-days-next"
                  disabled={page >= 1}
                  onClick={() => setPage((p) => Math.min(1, p + 1))}
                  className="p-2 rounded-full hover:bg-white disabled:opacity-30 transition-all"
                >
                  <ChevronRight size={17} />
                </button>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-7 gap-2">
              {visibleDays.map((d) => (
                <button
                  key={d.iso}
                  data-testid={`booking-calendar-day-${d.iso}`}
                  onClick={() => setDate(d.iso)}
                  className={`flex flex-col items-center gap-0.5 py-3 rounded-2xl border transition-all ${
                    date === d.iso
                      ? "bg-[#1d1d1f] text-white border-[#1d1d1f] shadow-md"
                      : "bg-white border-[#e5e5e7] hover:border-[#1d1d1f]/40"
                  }`}
                >
                  <span className={`text-[10px] uppercase tracking-wide ${date === d.iso ? "text-white/70" : "text-[#86868b]"}`}>{d.weekday}</span>
                  <span className="text-lg font-semibold leading-none">{d.day}</span>
                  <span className={`text-[10px] ${date === d.iso ? "text-white/70" : "text-[#a1a1a6]"}`}>{d.month}</span>
                </button>
              ))}
            </div>

            <p className="mt-8 flex items-center gap-2 text-sm font-semibold text-[#1d1d1f]">
              <Clock size={17} className="text-[#0066cc]" /> Pick a time {slotsLoading && <Loader2 size={14} className="animate-spin text-[#86868b]" />}
            </p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="booking-slots">
              {SLOTS.map((slot) => {
                const isTaken = taken.includes(slot);
                return (
                  <button
                    key={slot}
                    data-testid={`booking-slot-${slot.replace(/\s/g, "").toLowerCase()}`}
                    disabled={isTaken}
                    onClick={() => pickSlot(slot)}
                    className={`px-3 py-3 rounded-xl border text-sm font-medium transition-all ${
                      isTaken
                        ? "border-[#e5e5e7] bg-[#e5e5e7]/50 text-[#a1a1a6] cursor-not-allowed line-through"
                        : form.slot === slot
                          ? "border-[#0066cc] bg-[#0066cc] text-white shadow-md"
                          : "border-[#e5e5e7] bg-white hover:border-[#0066cc]/50 text-[#1d1d1f]"
                    }`}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-[#a1a1a6]">Greyed slots are already reserved. All times are IST (New Delhi).</p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="bg-[#f5f5f7] rounded-[28px] border border-[#e5e5e7] p-7 sm:p-9" data-testid="booking-form">
              <p className="text-sm font-semibold text-[#1d1d1f]">Session type</p>
              {payConfig?.enabled && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="booking-session-type">
                  <button
                    type="button" onClick={() => setSessionType("discovery")}
                    data-testid="booking-type-discovery"
                    className={`px-5 py-4 rounded-2xl border text-left transition-all ${sessionType === "discovery" ? "border-[#0066cc] bg-[#0066cc]/[0.06] shadow-sm" : "border-[#e5e5e7] bg-white hover:border-[#0066cc]/50"}`}
                  >
                    <p className="text-sm font-semibold text-[#1d1d1f]">Free discovery call</p>
                    <p className="text-xs text-[#86868b] mt-0.5">90 minutes · meet &amp; scope your project</p>
                  </button>
                  <button
                    type="button" onClick={() => setSessionType("consultation")}
                    data-testid="booking-type-consultation"
                    className={`px-5 py-4 rounded-2xl border text-left transition-all ${sessionType === "consultation" ? "border-[#0066cc] bg-[#0066cc]/[0.06] shadow-sm" : "border-[#e5e5e7] bg-white hover:border-[#0066cc]/50"}`}
                  >
                    <p className="text-sm font-semibold text-[#1d1d1f]">Paid consultation</p>
                    <p className="text-xs text-[#86868b] mt-0.5">1 hour · ₹7,000 · paid online with Razorpay</p>
                  </button>
                </div>
              )}
              <p className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#1d1d1f]">Your details</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <input
                  required placeholder="Full name" data-testid="booking-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                />
                <input
                  required type="email" placeholder="Email" data-testid="booking-email-input"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                />
                <input
                  required placeholder="WhatsApp number" data-testid="booking-phone-input"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                />
                <input
                  placeholder="Brand / company (optional)" data-testid="booking-company-input"
                  value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                />
                <select
                  data-testid="booking-topic-select"
                  value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="sm:col-span-2 px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] outline-none focus:border-[#0066cc] transition-colors"
                >
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <textarea
                  rows={3} placeholder="What would you like to discuss? (optional)" data-testid="booking-message-input"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="sm:col-span-2 px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] outline-none focus:border-[#0066cc] transition-colors resize-none"
                />
              </div>
              {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
              <button
                type="submit" disabled={submitting || paying || !form.slot} data-testid="booking-submit-btn"
                className="mt-6 w-full sm:w-auto px-8 py-4 rounded-full bg-[#0066cc] text-white font-medium text-sm hover:bg-[#0077ed] transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting || paying ? <Loader2 size={16} className="animate-spin" /> : <CalendarDays size={16} />}
                {submitting || paying
                  ? paying ? "Opening payment..." : "Confirming..."
                  : !form.slot
                    ? "Pick a time slot first"
                    : sessionType === "consultation" && payConfig?.enabled
                      ? `Confirm & pay ₹7,000 · ${form.slot}`
                      : `Confirm ${date} · ${form.slot}`}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
